/**
 * `shell/AdminButton.tsx` — the visible Admin button (#2902).
 *
 * A Tarii admin account (`account.is_admin`, the flag `UserPill` reads) gets
 * a bordered link to the admin console in the top bar. Everyone else gets
 * nothing, and nothing is requested: the gate reads the account already in
 * context. Below `sm` the word hides and the shield stays; the accessible
 * name stays "Admin". `compact` is the member-app header form: shield only,
 * the same 44 px target as the avatar.
 */
import { Shield } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

import { useAuth } from "../../lib/AuthContext";
import { cn } from "../../lib/cn";

export function AdminButton({ compact = false }: { compact?: boolean }) {
  const { account, mode } = useAuth();
  const { pathname } = useLocation();
  // The Telegram Mini App has member routes only: /tg/settings/admin falls back to /me.
  if (account?.is_admin !== true || mode === "telegram") return null;
  const current = pathname.startsWith("/settings/admin");
  return (
    <Link
      to="/settings/admin"
      aria-current={current ? "page" : undefined}
      aria-label="Admin"
      title="Admin"
      data-testid="admin-button"
      className={cn(
        "inline-flex min-h-tap min-w-tap shrink-0 items-center justify-center gap-1.5 rounded-md border border-border bg-bg-elevated text-xs font-medium text-fg hover:bg-bg-subtle",
        compact ? "" : "sm:h-8 sm:min-h-0 sm:min-w-0 sm:px-3",
        current && "bg-bg-subtle",
      )}
    >
      <Shield className="h-4 w-4" aria-hidden="true" />
      {compact ? null : <span className="hidden sm:inline">Admin</span>}
    </Link>
  );
}
